// TestModeBanner — fixed top-of-page banner shown when runtime mode is
// `test`. Coral background, cream text, 40px tall.
//
// Surfaces live mode-counts via the /api/settings/mode-counts endpoint
// (polled every 30s) so an operator in test mode sees how many real-data
// orders/customers/subscriptions exist on the LIVE side without having to
// flip first.
//
// Mount inside <AppShell /> ABOVE the topbar+sidebar layout. The shell
// reads the same `useShowTestBanner()` hook to know whether to offset its
// content by 40px (BANNER_HEIGHT) when the banner is present.

import { Link } from '@tanstack/react-router';
import { ArrowRight, FlaskConical } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeMode } from '@/lib/runtime-mode';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';

/** Banner height in pixels. Single source of truth for AppShell offset. */
export const BANNER_HEIGHT = 40;

/**
 * True when the banner should render. AppShell uses this to set its
 * top-padding offset without instantiating the component twice.
 */
export function useShowTestBanner(): boolean {
  const { mode } = useRuntimeMode();
  return mode === 'test';
}

export function TestModeBanner(): JSX.Element | null {
  const show = useShowTestBanner();
  // Always call useQuery (rules of hooks) but only fetch when banner is shown.
  const counts = useQuery({
    queryKey: queryKeys.settings.modeCounts(),
    queryFn: () => api.settings.modeCounts.get(),
    enabled: show,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
  if (!show) return null;

  const testCounts = counts.data?.test;
  const summary = testCounts
    ? `${testCounts.orders} orders · ${testCounts.customers} customers · ${testCounts.subscriptions} subscriptions`
    : null;

  return (
    <div
      role="status"
      aria-label="Admin in test mode"
      className="fixed top-0 left-0 right-0 z-[80] flex items-center justify-center gap-4 px-6"
      style={{
        height: BANNER_HEIGHT,
        background: 'var(--color-coral-500)',
        color: '#FFFFFF',
        boxShadow: '0 1px 0 rgba(30, 27, 24, 0.08)',
        fontSize: 13,
        lineHeight: '18px',
        fontWeight: 600,
        letterSpacing: '0.01em',
      }}
    >
      <span className="flex items-center gap-2">
        <FlaskConical size={14} aria-hidden="true" />
        <span
          className="font-display tracking-[0.12em] uppercase"
          style={{ fontSize: 11 }}
        >
          Showing TEST data only
        </span>
        {summary ? (
          <>
            <span aria-hidden="true" className="opacity-70 hidden sm:inline">
              ·
            </span>
            <span
              className="hidden sm:inline opacity-95"
              style={{ fontWeight: 500 }}
            >
              {summary}
            </span>
          </>
        ) : null}
      </span>
      <Link
        to="/settings/api-keys"
        hash="mode-header"
        className="inline-flex items-center gap-1 underline underline-offset-4 hover:opacity-90"
        style={{ color: '#FFFFFF' }}
        aria-label="View live data instead"
      >
        View live data instead
        <ArrowRight size={12} aria-hidden="true" />
      </Link>
    </div>
  );
}
